import { useMemo, useState, useCallback } from 'react';
import type { JobResponse } from '@taxassist/api-client';
import type { SortConfig } from '../pages/jobs/ListJobsView';

interface UseSortedAndPaginatedJobsProps {
  jobs: JobResponse[];
  initialSort?: SortConfig;
  initialPage?: number;
  initialPageSize?: number;
}

interface UseSortedAndPaginatedJobsReturn {
  sortedJobs: JobResponse[];
  paginatedJobs: JobResponse[];
  sortConfig: SortConfig;
  page: number;
  pageSize: number;
  totalPages: number;
  handleSortChange: (newSort: SortConfig) => void;
  handlePageChange: (newPage: number) => void;
  handlePageSizeChange: (newPageSize: number) => void;
  resetPagination: () => void;
}

/**
 * Hook for handling sorting and pagination of jobs
 */
export function useSortedAndPaginatedJobs({
  jobs,
  initialSort = { field: 'updatedAt', direction: 'desc' },
  initialPage = 0,
  initialPageSize = 25,
}: UseSortedAndPaginatedJobsProps): UseSortedAndPaginatedJobsReturn {
  const [sortConfig, setSortConfig] = useState<SortConfig>(initialSort);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Apply sorting to the jobs
  const sortedJobs = useMemo(() => {
    const sorted = [...jobs];
    
    sorted.sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];
      
      if (aValue === bValue) return 0;
      
      let comparison = 0;
      if (aValue !== undefined && aValue !== null && bValue !== undefined && bValue !== null) {
        comparison = aValue < bValue ? -1 : 1;
      } else if (aValue === undefined || aValue === null) {
        comparison = 1;
      } else {
        comparison = -1;
      }
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [jobs, sortConfig]);

  // Apply pagination
  const paginatedJobs = useMemo(() => {
    const start = page * pageSize;
    return sortedJobs.slice(start, start + pageSize);
  }, [sortedJobs, page, pageSize]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(sortedJobs.length / pageSize);
  }, [sortedJobs.length, pageSize]);

  // Handlers
  const handleSortChange = useCallback((newSort: SortConfig) => {
    setSortConfig(newSort);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when page size changes
  }, []);

  const resetPagination = useCallback(() => {
    setPage(0);
  }, []);

  return {
    sortedJobs,
    paginatedJobs,
    sortConfig,
    page,
    pageSize,
    totalPages,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
    resetPagination,
  };
}